/**
 * useIntegrationConsultations — Integration Hub
 * -----------------------------------------------------------------------------
 * Histórico de consultas realizadas nos aplicativos integrados.
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nenhum SELECT direto em tabela).
 *   - Somente administradores do tenant enxergam o histórico (validado no banco).
 *   - O documento consultado sempre chega mascarado.
 *   - O PDF nunca é exposto por link público: a URL assinada é gerada pela
 *     edge function `integration-pdf-url` (validade de 5 minutos).
 *
 * @version 1.2.0
 * RELEASE: 2026-08-29 v1.2.0 - Cancelamento server-side de consultas na fila
 *          ou em processamento, com invalidação imediata do histórico.
 * RELEASE: 2026-08-29 v1.1.0 - Consulta assíncrona: enfileiramento via
 *          `integration-enqueue` e atualização automática enquanto houver
 *          consultas na fila ou em processamento.
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (aba Histórico de Consultas).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('IntegrationConsultations');

// =============================================================================
// TIPOS
// =============================================================================

/** Linha da listagem do histórico */
export interface ConsultationListItem {
  id: string;
  created_at: string;
  provider_slug: string;
  operation_code: string;
  operation_name: string | null;
  connection_name: string | null;
  document_masked: string | null;
  status: string;
  http_status: number | null;
  duration_ms: number | null;
  cost_cents: number | null;
  error_message: string | null;
  has_pdf: boolean;
  user_name: string | null;
  /** Tentativas de verificação no provider (consultas assíncronas) */
  attempts?: number | null;
  total_count: number;
}

/** Detalhe completo de uma consulta */
export interface ConsultationDetail {
  id: string;
  created_at: string;
  provider_slug: string;
  operation_code: string;
  operation_name: string | null;
  connection_id: string | null;
  connection_name: string | null;
  document_masked: string | null;
  params: Record<string, unknown> | null;
  status: string;
  http_status: number | null;
  duration_ms: number | null;
  request_id: string | null;
  cost_cents: number | null;
  error_message: string | null;
  response_payload: unknown;
  pdf_path: string | null;
  pdf_filename: string | null;
  pdf_size_bytes: number | null;
  pdf_expires_at: string | null;
  payload_purged_at: string | null;
  user_name: string | null;
}

/** Filtros da listagem */
export interface ConsultationFilters {
  providerSlug?: string | null;
  connectionId?: string | null;
  operationCode?: string | null;
  status?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  document?: string | null;
  page?: number;
  pageSize?: number;
}

// =============================================================================
// HOOK — LISTAGEM
// =============================================================================

/**
 * Lista paginada do histórico de consultas.
 * `enabled = false` evita qualquer chamada para usuários sem permissão.
 */
export function useIntegrationConsultations(
  filters: ConsultationFilters,
  enabled = true,
) {
  const pageSize = filters.pageSize ?? 20;
  const page = filters.page ?? 1;

  const query = useQuery({
    queryKey: ['integration-consultations', filters],
    enabled,
    staleTime: 5 * 1000,
    // Enquanto existir consulta na fila/processando, atualiza sozinho (5s)
    refetchInterval: (q) => {
      const rows = (q.state.data ?? []) as ConsultationListItem[];
      const pending = rows.some((r) => r.status === 'queued' || r.status === 'processing');
      return pending ? 5000 : false;
    },
    queryFn: async (): Promise<ConsultationListItem[]> => {
      const { data, error } = await supabase.rpc('list_integration_consultations', {
        p_provider_slug: filters.providerSlug ?? null,
        p_connection_id: filters.connectionId ?? null,
        p_operation_code: filters.operationCode ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
        p_document: filters.document ?? null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('list_integration_consultations', { code: error.code });
        throw new Error('Não foi possível carregar o histórico de consultas');
      }
      return (data ?? []) as unknown as ConsultationListItem[];
    },
  });

  const items = query.data ?? [];
  const total = items[0]?.total_count ?? 0;

  return {
    items,
    total: Number(total),
    pageSize,
    page,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

// =============================================================================
// HOOK — DETALHE + PDF
// =============================================================================

/** Detalhe de uma consulta (payload completo) */
export function useIntegrationConsultation(consultationId: string | null) {
  return useQuery({
    queryKey: ['integration-consultation', consultationId],
    enabled: Boolean(consultationId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ConsultationDetail | null> => {
      const { data, error } = await supabase.rpc('get_integration_consultation', {
        p_id: consultationId as string,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('get_integration_consultation', { code: error.code });
        throw new Error('Não foi possível carregar a consulta');
      }
      const rows = (data ?? []) as unknown as ConsultationDetail[];
      return rows[0] ?? null;
    },
  });
}

// =============================================================================
// HOOK — CANCELAMENTO
// =============================================================================

/**
 * Interrompe uma consulta ativa por RPC tenant-safe. O banco também remove as
 * mensagens pendentes da fila e impede workers atrasados de reativarem a linha.
 */
export function useCancelIntegrationConsultation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consultationId: string): Promise<void> => {
      const { error } = await supabase.rpc('cancel_integration_consultation', {
        p_id: consultationId,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('cancel_integration_consultation', { code: error.code });
        throw new Error(
          error.code === '55000'
            ? 'A consulta já foi finalizada ou cancelada'
            : 'Não foi possível cancelar o processamento',
        );
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['integration-consultations'] });
      toast.success('Processamento cancelado');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Gera a URL assinada do PDF (5 minutos) via edge function.
 * Retorna null quando a consulta não possui arquivo disponível.
 */
export async function fetchConsultationPdfUrl(consultationId: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('integration-pdf-url', {
    body: { consultation_id: consultationId },
  });

  if (error) {
    logger.error('integration-pdf-url', { message: error.message });
    return null;
  }
  const payload = data as { success?: boolean; url?: string } | null;
  return payload?.success && payload.url ? payload.url : null;
}

// =============================================================================
// HOOK — EXECUÇÃO DE CONSULTA
// =============================================================================

/** Resultado devolvido pelo gateway ao executar um recurso */
export interface RunOperationResult {
  data: unknown;
  pdf: { has_pdf: boolean; filename?: string; size_bytes?: number };
}

/**
 * Executa um recurso do provider através do gateway.
 * Nenhum dado de credencial trafega pelo navegador.
 */
export function useRunIntegrationOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      connectionId: string;
      operation: string;
      params: Record<string, unknown>;
    }): Promise<RunOperationResult> => {
      const { data, error } = await supabase.functions.invoke('integration-gateway', {
        body: {
          action: 'execute',
          connection_id: input.connectionId,
          operation: input.operation,
          params: input.params,
        },
      });

      if (error) {
        // A mensagem real do gateway vem no corpo da resposta
        const details =
          typeof (error as { context?: { text?: () => Promise<string> } }).context?.text === 'function'
            ? await (error as { context: { text: () => Promise<string> } }).context.text()
            : error.message;
        let message = 'Falha ao executar a consulta';
        try {
          const parsed = JSON.parse(details) as { error?: string };
          if (parsed?.error) message = parsed.error;
        } catch {
          if (details) message = String(details).slice(0, 300);
        }
        throw new Error(message);
      }

      const payload = data as { success?: boolean; error?: string } & RunOperationResult;
      if (!payload?.success) throw new Error(payload?.error ?? 'Falha ao executar a consulta');

      return { data: payload.data, pdf: payload.pdf ?? { has_pdf: false } };
    },
    onSuccess: () => {
      // O histórico é gravado no backend: recarrega listagens abertas
      queryClient.invalidateQueries({ queryKey: ['integration-consultations'] });
      queryClient.invalidateQueries({ queryKey: ['integration-request-logs'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// =============================================================================
// HOOK — ENFILEIRAMENTO DA CONSULTA (assíncrono)
// =============================================================================

/** Retorno do enfileiramento */
export interface EnqueueOperationResult {
  consultationId: string;
}

/**
 * Enfileira a consulta na fila `q_integration` (edge function
 * `integration-enqueue`). O provider é chamado em segundo plano pelo worker,
 * evitando segurar a requisição por até ~1 minuto.
 */
export function useEnqueueIntegrationOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      connectionId: string;
      operation: string;
      operationName?: string | null;
      params: Record<string, unknown>;
    }): Promise<EnqueueOperationResult> => {
      const { data, error } = await supabase.functions.invoke('integration-enqueue', {
        body: {
          connection_id: input.connectionId,
          operation: input.operation,
          operation_name: input.operationName ?? null,
          params: input.params,
        },
      });

      if (error) {
        // A mensagem real vem no corpo da resposta da edge function
        const details =
          typeof (error as { context?: { text?: () => Promise<string> } }).context?.text === 'function'
            ? await (error as { context: { text: () => Promise<string> } }).context.text()
            : error.message;
        let message = 'Falha ao enfileirar a consulta';
        try {
          const parsed = JSON.parse(details) as { error?: string };
          if (parsed?.error) message = parsed.error;
        } catch {
          if (details) message = String(details).slice(0, 300);
        }
        throw new Error(message);
      }

      const payload = data as { success?: boolean; error?: string; consultation_id?: string };
      if (!payload?.success || !payload.consultation_id) {
        throw new Error(payload?.error ?? 'Falha ao enfileirar a consulta');
      }

      return { consultationId: payload.consultation_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-consultations'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
